/**
 * CommandPalette — ⌘K / Ctrl+K fuzzy-search modal.
 *
 * Opens from anywhere in CC. Indexes every sidebar leaf, plus a few
 * action shortcuts. Arrow keys to move, Enter to navigate, Esc to
 * close. Designed to take over once your hands learn the muscle
 * memory — sidebar stays for discovery, this is the accelerator.
 *
 * Why custom (not cmdk lib): contained scope, no new dep, full
 * control over styling to match the luxury black palette.
 *
 * Author: Scoring team (CC sidebar Phase 2, 2026-06-13).
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, ArrowRight, CornerDownLeft } from 'lucide-react';
import { cn } from '@/lib/utils';

export interface CommandItem {
  /** Unique id (usually the route path). */
  id: string;
  /** Display label. */
  label: string;
  /** Group label shown as a subtle hint to the right. */
  group?: string;
  /** Optional icon. */
  icon?: React.ComponentType<{ className?: string }>;
  /** What to do on Enter. If `href` is provided, we navigate; otherwise call onSelect. */
  href?: string;
  onSelect?: () => void;
  /** Extra search keywords (synonyms, aliases). */
  keywords?: string[];
}

interface Props {
  items: CommandItem[];
}

/** Lightweight fuzzy score: substring match wins, otherwise scattered-char match. */
function score(query: string, item: CommandItem): number {
  const q = query.toLowerCase().trim();
  if (!q) return 1;
  const haystacks = [item.label, item.group ?? '', ...(item.keywords ?? [])]
    .filter(Boolean)
    .map((s) => s.toLowerCase());
  // Strong: substring in any haystack
  for (const h of haystacks) {
    if (h.includes(q)) return 100 - (h.indexOf(q) || 0);
  }
  // Weak: every char in `q` appears in order in `label`
  let i = 0;
  const label = item.label.toLowerCase();
  for (const ch of label) {
    if (ch === q[i]) i++;
    if (i === q.length) return 10;
  }
  return 0;
}

export default function CommandPalette({ items }: Props) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [cursor, setCursor] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();

  // Global ⌘K / Ctrl+K handler.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setOpen((prev) => !prev);
      }
      if (e.key === 'Escape' && open) {
        setOpen(false);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);

  // Focus input + reset on open
  useEffect(() => {
    if (open) {
      setQuery('');
      setCursor(0);
      // Microtask so the input is mounted before we focus
      requestAnimationFrame(() => inputRef.current?.focus());
    }
  }, [open]);

  const filtered = useMemo(() => {
    return items
      .map((it) => ({ it, s: score(query, it) }))
      .filter((r) => r.s > 0)
      .sort((a, b) => b.s - a.s)
      .slice(0, 12)
      .map((r) => r.it);
  }, [items, query]);

  // Clamp cursor when filtered list shrinks
  useEffect(() => {
    if (cursor >= filtered.length) setCursor(Math.max(0, filtered.length - 1));
  }, [filtered.length, cursor]);

  const choose = (it: CommandItem) => {
    setOpen(false);
    if (it.href) navigate(it.href);
    else it.onSelect?.();
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setCursor((c) => Math.min(filtered.length - 1, c + 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setCursor((c) => Math.max(0, c - 1));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const it = filtered[cursor];
      if (it) choose(it);
    }
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[100] flex items-start justify-center pt-[12vh] bg-black/60 backdrop-blur-sm"
      onClick={() => setOpen(false)}
      role="dialog"
      aria-modal="true"
      aria-label="Command palette"
    >
      <div
        className="w-[640px] max-w-[92vw] bg-card border border-border rounded-xl shadow-2xl overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Input row */}
        <div className="flex items-center gap-3 px-4 py-3 border-b border-border">
          <Search className="w-4 h-4 text-muted-foreground shrink-0" />
          <input
            ref={inputRef}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={onKeyDown}
            placeholder="Jump to anything…"
            className="flex-1 bg-transparent outline-none text-sm text-foreground placeholder:text-muted-foreground"
          />
          <kbd className="text-[10px] font-mono text-muted-foreground bg-muted/40 px-1.5 py-0.5 rounded">
            esc
          </kbd>
        </div>

        {/* Results */}
        <div className="max-h-[55vh] overflow-y-auto py-1">
          {filtered.length === 0 ? (
            <div className="px-4 py-8 text-center text-sm text-muted-foreground">
              No matches for "{query}".
            </div>
          ) : (
            filtered.map((it, idx) => (
              <button
                key={it.id}
                onClick={() => choose(it)}
                onMouseEnter={() => setCursor(idx)}
                className={cn(
                  'w-full flex items-center gap-3 px-4 py-2 text-left transition-colors',
                  idx === cursor ? 'bg-primary/[0.08]' : 'hover:bg-muted/40',
                )}
              >
                {it.icon && (
                  <it.icon
                    className={cn(
                      'w-4 h-4 shrink-0',
                      idx === cursor ? 'text-primary' : 'text-muted-foreground',
                    )}
                  />
                )}
                <span
                  className={cn(
                    'flex-1 text-sm truncate',
                    idx === cursor ? 'text-foreground font-medium' : 'text-foreground/85',
                  )}
                >
                  {it.label}
                </span>
                {it.group && (
                  <span className="text-[11px] text-muted-foreground">{it.group}</span>
                )}
                {idx === cursor && (
                  <CornerDownLeft className="w-3.5 h-3.5 text-primary/70 shrink-0" />
                )}
              </button>
            ))
          )}
        </div>

        {/* Footer hint */}
        <div className="border-t border-border px-4 py-2 flex items-center justify-between text-[10px] text-muted-foreground">
          <span className="flex items-center gap-3">
            <span className="inline-flex items-center gap-1">
              <kbd className="font-mono bg-muted/40 px-1 rounded">↑</kbd>
              <kbd className="font-mono bg-muted/40 px-1 rounded">↓</kbd>
              navigate
            </span>
            <span className="inline-flex items-center gap-1">
              <kbd className="font-mono bg-muted/40 px-1 rounded">↵</kbd>
              select
            </span>
          </span>
          <span className="inline-flex items-center gap-1">
            <ArrowRight className="w-3 h-3" />
            jump
          </span>
        </div>
      </div>
    </div>
  );
}
